import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripLines, faFolderOpen, faFolderMinus, faList, faLock } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import ActionMenu from "../../action-menu/ActionMenu";
import FolderSelectionDialog from "../../folder-selection-dialog/FolderSelectionDialog";

export const FolderMenuItem = ({
    list,
    folders,
    selectedListId,
    onSelectList,
    onMoveListToFolder,
    showReorderHandle = false,
}) => {
    const { t } = useTranslation();
    const [showFolderDialog, setShowFolderDialog] = useState(false);

    const currentFolderId = useMemo(() => {
        for (const folder of folders || []) {
            // folder.collections now contains collection objects, so we check for the collection object
            if (
                (folder.collections || []).some(
                    (folderCollection) => folderCollection.id === list.id,
                )
            ) {
                return folder.id;
            }
        }
        return null;
    }, [folders, list.id]);

    const currentFolder = useMemo(
        () => (folders || []).find((folder) => folder.id === currentFolderId),
        [folders, currentFolderId],
    );

    const rootFolder = useMemo(
        () => (folders || []).find((folder) => folder.name === "General"),
        [folders],
    );

    const canRemoveFromFolder = Boolean(
        currentFolder &&
        rootFolder &&
        currentFolder.id !== rootFolder.id,
    );

    const hasMoveDestination = useMemo(
        () => (folders || []).some((folder) => folder.id !== rootFolder?.id),
        [folders, rootFolder],
    );

    const actions = useMemo(() => {
        const menuActions = [];

        if (hasMoveDestination) {
            menuActions.push({
                label: t('folderMenuItem.moveToFolder'),
                icon: faFolderOpen,
                callback: () => setShowFolderDialog(true),
            });
        }

        if (canRemoveFromFolder && rootFolder) {
            menuActions.push({
                label: t('folderMenuItem.removeFromFolder', { folder: currentFolder.name }),
                icon: faFolderMinus,
                callback: () => onMoveListToFolder?.(list.id, rootFolder.id),
            });
        }

        return menuActions;
    }, [
        canRemoveFromFolder, currentFolder, hasMoveDestination, list.id, onMoveListToFolder, rootFolder, t,
    ]);

    return (
        <>
            <div
                className={`folder-menu-item ${selectedListId === list.id ? "selected" : ""}`}
            >
                <div className="list-left-section">
                    {showReorderHandle && (
                        <span className="list-drag-handle">
                            <FontAwesomeIcon icon={faGripLines} />
                        </span>
                    )}
                    <span className="list-menu-icon">
                        <FontAwesomeIcon icon={list.secret ? faLock : faList} />
                    </span>
                    <span
                        className="list-name"
                        onClick={() => onSelectList(list.id)}
                        title={list.name}
                    >
                        <span className="list-name-text">{list.name}</span>{" "}
                        {(list.elements?.length || list.todos?.length) > 0 && (
                            <span
                                style={{ color: "#888", fontWeight: "normal" }}
                            >
                                (
                                {list?.elements?.length ||
                                    list?.todos?.length ||
                                    0}
                                )
                            </span>
                        )}
                    </span>
                </div>
                {actions.length > 0 && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                        }}
                    >
                        <ActionMenu
                            actions={actions}
                            buttonStyle={{ color: "black" }}
                            menuItemStyle={{ fontWeight: 400 }}
                        />
                    </div>
                )}
            </div>

            {showFolderDialog && (
                <FolderSelectionDialog
                    folders={folders}
                    selectedFolderId={currentFolderId}
                    rootFolderId={rootFolder?.id}
                    onSelectFolder={(folderId) =>
                        onMoveListToFolder?.(list.id, folderId)
                    }
                    onClose={() => setShowFolderDialog(false)}
                />
            )}
        </>
    );
};
